import { useLocation, useNavigate, useParams } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import { TextField, Button, Checkbox } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import TableComponent from "../../components/TableComponent";
import { useEffect, useState } from "react";
import { isString, isValidInput } from "../../utils/validations";
import {
  RoleResponse,
  useAddRoleMutation,
  useUpdateRoleByIdMutation,
} from "../../services/accessRole";
import { showError, showToast } from "../../constants/toasts";
import { ROLES } from "../../utils/enums";
import useAuth from "../../hooks/useAuth";

const AddRole = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { state }: { state: RoleResponse } = useLocation();
  const [roleName, setRoleName] = useState("");
  const [addRole] = useAddRoleMutation();
  const [updateRole] = useUpdateRoleByIdMutation();
  const [error, setError] = useState(false);
  const user = useAuth();
  const [rows, setRows] = useState([
    {
      id: 1,
      name: "Dashboard",
      view: false,
      add: false,
      edit: false,
      delete: false,
    },
    {
      id: 2,
      name: "Manage Customers",
      view: false,
      add: false,
      edit: false,
      delete: false,
    },
    {
      id: 3,
      name: "Manage Maintenance Team",
      view: false,
      add: false,
      edit: false,
      delete: false,
    },
    {
      id: 4,
      name: "Manage Issues",
      view: false,
      add: false,
      edit: false,
      delete: false,
    },
    {
      id: 5,
      name: "Manage Categories",
      view: false,
      add: false,
      edit: false,
      delete: false,
    },
    {
      id: 6,
      name: "Manage Payments",
      view: false,
      add: false,
      edit: false,
      delete: false,
    },
    {
      id: 7,
      name: "Manage Analytics",
      view: false,
      add: false,
      edit: false,
      delete: false,
    },
    {
      id: 8,
      name: "Manage Customer Support",
      view: false,
      add: false,
      edit: false,
      delete: false,
    },
    {
      id: 9,
      name: "Manage Notifications",
      view: false,
      add: false,
      edit: false,
      delete: false,
    },
    {
      id: 10,
      name: "Manage Reviews",
      view: false,
      add: false,
      edit: false,
      delete: false,
    },
    {
      id: 11,
      name: "Manage CMS",
      view: false,
      add: false,
      edit: false,
      delete: false,
    },
  ]);

  // Handle checkbox toggle
  const handleCheckboxChange = (
    id: number,
    field: keyof (typeof rows)[number]
  ) => {
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.id === id ? { ...row, [field]: !row[field] } : row
      )
    );
  };

  const handleAddEditRole = async () => {
    if (!roleName) {
      return;
    }

    const body = {
      name: roleName,
      permission: rows.map((item) => ({
        sideBarId: item.id,
        label: item.name,
        isView: item.view,
        isAdd: item.add,
        isEdit: item.add,
        isDelete: item.delete,
      })),
    };

    try {
      let response;
      if (id) {
        // If ID exists, update customer
        response = await updateRole({ id, body }).unwrap();
      } else {
        // Else, add customer
        response = await addRole({ body }).unwrap();
      }
      if (response?.statusCode === 200) {
        // setUserId(response?.data?._id || "");
        showToast(id ? "Role update successfully" : "Role added successfully");

        navigate("/manage-subadmin/roles");
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
      console.log(error);
    }
  };
  const handleRoleNameChange = (i: string) => {
    setRoleName(i);
  };

  const columns: GridColDef<(typeof rows)[number]>[] = [
    {
      flex: 1,
      minWidth: 180,
      field: "name",
      headerName: "Module Name",
      editable: false,
      sortable: false,
    },
    {
      flex: 1,
      minWidth: 180,
      field: "view",
      headerName: "View",
      editable: false,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => (
        <Checkbox
          checked={row.view}
          onChange={() => {
            if (row.view) {
              if (row.add) {
                handleCheckboxChange(row.id, "add");
              }
              if (row.delete) {
                handleCheckboxChange(row.id, "delete");
              }
            }
            handleCheckboxChange(row.id, "view");
          }}
        />
      ),
    },
    {
      flex: 1,
      minWidth: 180,
      field: "add",
      headerName: "Add/Edit",
      editable: false,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => (
        <Checkbox
          disabled={row.name === "Dashboard"}
          checked={row.add}
          onChange={() => {
            if (!row.view) {
              handleCheckboxChange(row.id, "view");
            }
            handleCheckboxChange(row.id, "add");
          }}
        />
      ),
    },
    // {
    //   flex: 1,
    //   minWidth: 150,
    //   field: "edit",
    //   headerName: "Edit",
    //   editable: false,
    //   sortable: false,
    //   align: 'center',
    //   headerAlign: 'center',
    //   renderCell: ({ row }) => (
    //     <Checkbox
    //       checked={row.view}
    //       onChange={() => handleCheckboxChange(row.id, "edit")}
    //     />
    //   ),
    // },
    {
      flex: 1,
      minWidth: 100,
      field: "delete",
      headerName: "Delete",
      editable: false,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => (
        <Checkbox
          checked={row.delete}
          disabled={row.name === "Dashboard"}
          onChange={() => {
            if (!row.view) {
              handleCheckboxChange(row.id, "view");
            }
            handleCheckboxChange(row.id, "delete");
          }}
        />
      ),
    },
  ];
  let hidden = ["fullName"];

  useEffect(() => {
    if (user?.role === ROLES.SUB_ADMIN) {
      navigate("/permission-denied", { replace: true });
    }
  }, [user]);

  useEffect(() => {
    if (id) {
      setRoleName(state?.name);
      setRows([
        ...state?.permission.map((item, i) => ({
          name: item?.label,
          id: i + 1,
          view: item?.isView,
          add: item?.isAdd,
          edit: item?.isEdit,
          delete: item?.isDelete,
        })),
      ]);
    }
  }, [id]);

  return (
    <MainContainer>
      <div className="main_title">
        <h1>
          {id ? "Edit" : "Add"}
          {" Role"}
        </h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>Dashboard</span> -{" "}
          <span onClick={() => navigate("/manage-subadmin")}>
            Manage Sub-Admin
          </span>{" "}
          -{" "}
          <span onClick={() => navigate("/manage-subadmin/roles")}>
            Manage Roles
          </span>{" "}
          - {id ? "Edit" : "Add"}
          {" Role"}
        </p>
      </div>

      <div className="cards">
        <div className="form">
          <div className="gap_p">
            <div className="control_group w_50">
              <label>Role Name</label>
              <TextField
                placeholder="Enter Name"
                variant="outlined"
                className="text_field"
                fullWidth
                hiddenLabel
                name="name"
                id="name"
                inputProps={{ maxLength: 30 }}
                value={roleName}
                disabled={Boolean(id)}
                onChange={(val: React.ChangeEvent<HTMLInputElement>) => {
                  if (
                    isValidInput(val.target.value) &&
                    isString(val.target.value)
                  ) {
                    handleRoleNameChange(val.target.value);
                  }
                }}
              />
              {!roleName && error ? (
                <h6 className="err_msg">Role Name is required</h6>
              ) : (
                ""
              )}
            </div>
          </div>
          <div className="sorting_table mt_20 role">
            <TableComponent
              columns={columns}
              rows={rows}
              onPageChange={() => {}}
            />
          </div>
          <div className="form_btn">
            <Button
              className="btn btn_primary"
              onClick={() => {
                setError(true);
                handleAddEditRole();
              }}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </MainContainer>
  );
};

export default AddRole;
